import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/constants";
import { Users2, Plus, X, Edit, Key, ToggleLeft, ToggleRight } from "lucide-react";

const ROLES = [
  "it_admin","general_counsel","legal_manager","attorney","contract_analyst",
  "compliance","records_management","purchasing","department_director",
  "department_admin","business_unit_rep","readonly_auditor","contract_manager",
];

export default function UsersAdmin() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [modal, setModal] = useState(null);   // null | "create" | {edit: userObj}
  const [form, setForm] = useState({ email: "", name: "", role: "business_unit_rep", title: "", department: "", password: "" });

  const load = () => api.get("/users").then(r => setRows(r.data || [])).catch(() => toast.error("Failed to load users"));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ email: "", name: "", role: "business_unit_rep", title: "", department: "", password: "" });
    setModal("create");
  };
  const openEdit = (u) => {
    setForm({ email: u.email, name: u.name || "", role: u.role, title: u.title || "", department: u.department || "", password: "" });
    setModal({ edit: u });
  };
  const save = async () => {
    try {
      if (modal === "create") {
        if (!form.email || !form.name || !form.role) return toast.error("Email, name, role are required");
        await api.post("/users", form);
        toast.success("User created");
      } else if (modal?.edit) {
        const body = { name: form.name, role: form.role, title: form.title, department: form.department };
        if (form.password) body.password = form.password;
        await api.patch(`/users/${modal.edit.id}`, body);
        toast.success("User updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = rows.filter(u => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-6" data-testid="users-admin-page">
      <div className="flex items-center gap-3 mb-4">
        <Users2 className="w-5 h-5 text-[#005289]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Administration · Users</div>
          <h1 className="text-2xl font-bold text-slate-900">User Table</h1>
          <p className="text-sm text-slate-500 mt-1">Create, edit, and assign roles — use these accounts to test role-based access without SSO.</p>
        </div>
        <button onClick={openCreate} data-testid="user-create-btn"
                className="ml-auto h-9 px-4 rounded text-xs font-bold text-white bg-[#005289] flex items-center gap-1">
          <Plus className="w-3 h-3"/> Add User
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email…" data-testid="users-search"
               className="h-9 px-3 border border-slate-200 rounded text-xs flex-1 max-w-md"/>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} data-testid="users-role-filter"
                className="h-9 px-2 border border-slate-200 rounded text-xs">
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="hcd-table text-xs w-full">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Title</th><th>Department</th><th>Active</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-rows">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate-400">No users match your filter.</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-semibold">{u.name}</td>
                <td className="font-mono text-[11px]">{u.email}</td>
                <td><span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="text-slate-600">{u.title || "—"}</td>
                <td className="text-slate-600">{u.department || "—"}</td>
                <td>
                  <button onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`} className="flex items-center">
                    {u.active ? <ToggleRight className="w-5 h-5 text-emerald-500"/> : <ToggleLeft className="w-5 h-5 text-slate-400"/>}
                  </button>
                </td>
                <td className="text-right">
                  <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}
                          className="h-7 px-2 text-[10px] border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1">
                    <Edit className="w-3 h-3"/> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)} data-testid="user-modal">
          <div className="bg-white rounded-md w-[520px] max-w-[95vw]" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <div className="font-bold text-sm">{modal === "create" ? "Add User" : `Edit ${modal.edit?.name}`}</div>
              <button onClick={() => setModal(null)} className="ml-auto text-slate-400"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-5 space-y-3 text-xs">
              <Field label="Email" disabled={modal !== "create"}>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="user-form-email"
                       disabled={modal !== "create"}
                       className="w-full h-9 px-3 border border-slate-200 rounded"/>
              </Field>
              <Field label="Name">
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="user-form-name"
                       className="w-full h-9 px-3 border border-slate-200 rounded"/>
              </Field>
              <Field label="Role">
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} data-testid="user-form-role"
                        className="w-full h-9 px-2 border border-slate-200 rounded">
                  {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Title">
                  <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                         className="w-full h-9 px-3 border border-slate-200 rounded"/>
                </Field>
                <Field label="Department">
                  <input value={form.department} onChange={e => setForm({...form, department: e.target.value})}
                         className="w-full h-9 px-3 border border-slate-200 rounded"/>
                </Field>
              </div>
              <Field label={modal === "create" ? "Password (optional — defaults to HCD2026!)" : "Reset password (leave blank to keep)"}>
                <div className="relative">
                  <Key className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                         placeholder={modal === "create" ? "HCD2026!" : "•••••• (unchanged)"} data-testid="user-form-password"
                         className="w-full h-9 pl-7 pr-3 border border-slate-200 rounded font-mono"/>
                </div>
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={save} data-testid="user-form-save" className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white">Save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-bold text-slate-700 block mb-1">{label}</span>
      {children}
    </label>
  );
}
